
import { supabase } from "@/integrations/supabase/client";
import { Candidate } from "@/types/candidate";
import { toast } from "@/components/ui/use-toast";

export const updateCandidate = async (
  id: string,
  updatedData: Partial<Candidate>
): Promise<boolean> => {
  if (!id) return false;

  try {
    console.log("updateCandidate operation with data:", updatedData);
    
    // First get the current candidate data to compare for history logging
    const { data: currentCandidate, error: fetchError } = await supabase
      .from('candidates')
      .select('*')
      .eq('id', id)
      .single();
      
    if (fetchError) {
      console.error("Error fetching current candidate data:", fetchError);
      toast({
        title: "Error",
        description: "Could not fetch current candidate data",
        variant: "destructive"
      });
      return false;
    }
    
    // Make sure we're not sending any fields that don't exist in the database
    const dataToUpdate: Record<string, any> = {};
    
    // Only include fields that exist in the database table
    if (updatedData.name !== undefined) dataToUpdate.name = updatedData.name;
    if (updatedData.email !== undefined) dataToUpdate.email = updatedData.email;
    if (updatedData.phone !== undefined) dataToUpdate.phone = updatedData.phone;
    if (updatedData.function !== undefined) dataToUpdate.function = updatedData.function;
    if (updatedData.skills !== undefined) dataToUpdate.skills = updatedData.skills;
    if (updatedData.manager !== undefined) dataToUpdate.manager = updatedData.manager;
    if (updatedData.status !== undefined) dataToUpdate.status = updatedData.status;
    if (updatedData.linkedin !== undefined) dataToUpdate.linkedin = updatedData.linkedin;
    
    const { error } = await supabase
      .from('candidates')
      .update(dataToUpdate)
      .eq('id', id);

    if (error) {
      console.error("Error updating candidate:", error);
      toast({
        title: "Error",
        description: `Failed to update candidate: ${error.message}`,
        variant: "destructive"
      });
      throw error;
    }

    // Log changes to history table
    try {
      // Get the current user for the performed_by field
      let userName = "System User";
      const { data: userSession } = await supabase.auth.getSession();
      
      if (userSession.session?.user) {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', userSession.session.user.id)
          .single();
        
        if (userProfile) {
          userName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim();
          if (!userName) userName = "System User";
        }
      }
      
      // Log specific changes for each updated field
      const updatedFields = Object.entries(dataToUpdate);
      for (const [field, newValue] of updatedFields) {
        const oldValue = currentCandidate[field];
        
        // Only log if values actually changed
        if (String(oldValue || '') !== String(newValue || '')) {
          // Format the field name for better readability
          const formattedFieldName = field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
          
          // Create the history description with the change details
          const description = `${formattedFieldName}: Changed from "${oldValue || ''}" to "${newValue || ''}"`;
          
          await supabase
            .from('candidate_history')
            .insert({
              candidate_id: id,
              action: 'updated',
              description: description,
              performed_by: userName
            });
        }
      }
    } catch (historyError) {
      console.error("Error logging candidate history:", historyError);
      // Don't fail the update if history logging fails
    }

    toast({
      title: "Success",
      description: "Candidate updated successfully"
    });
    return true;
  } catch (error) {
    console.error("Error updating candidate:", error);
    return false;
  }
};
